import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Button,
    Stack,
    alpha,
} from '@mui/material';
import SmsIcon from '@mui/icons-material/Sms';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import CommunicationManagementService, {
    CommunicationChannel,
    CommunicationHistoryFilters,
    CommunicationHistoryItem,
} from '../../services/CommunicationManagementService';

import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from '../Common/CustomSnackBar';

const service = new CommunicationManagementService();

const CHANNEL_META = {
    SMS:      { label: 'SMS',      icon: <SmsIcon sx={{ fontSize: 13 }} />,                  bg: '#dbeafe', color: '#1d4ed8' },
    WHATSAPP: { label: 'WhatsApp', icon: <WhatsAppIcon sx={{ fontSize: 13 }} />,              bg: '#dcfce7', color: '#15803d' },
    FCM:      { label: 'In-App',   icon: <NotificationsActiveIcon sx={{ fontSize: 13 }} />,  bg: '#f3e8ff', color: '#7e22ce' },
} as const;

const STATUS_META: Record<string, { color: 'success' | 'error' | 'warning' | 'default'; label: string }> = {
    DELIVERED: { color: 'success', label: 'Delivered' },
    SUCCESS:   { color: 'success', label: 'Success'   },
    SENT:      { color: 'success', label: 'Sent'      },
    FAILED:    { color: 'error',   label: 'Failed'    },
    FAILURE:   { color: 'error',   label: 'Failed'    },
    BLOCKED:   { color: 'error',   label: 'Blocked'   },
    NDNC:      { color: 'error',   label: 'NDNC'      },
    PENDING:   { color: 'warning', label: 'Pending'   },
    SUBMITTED: { color: 'warning', label: 'Submitted' },
};

const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });

interface Props { customerId: number }

const SkeletonRows = () => (
    <>
        {[...Array(4)].map((_, i) => (
            <TableRow key={i}>
                {[100, 140, 260, 80, 120].map((w, j) => (
                    <TableCell key={j}><Skeleton variant="rounded" width={w} height={20} /></TableCell>
                ))}
            </TableRow>
        ))}
    </>
);

export default function CustomerCommunicationHistory({ customerId }: Props) {
    const [history, setHistory] = useState<CommunicationHistoryItem[]>([]);
    const [total, setTotal]     = useState(0);
    const [loading, setLoading] = useState(true);
    const [channel, setChannel] = useState<CommunicationChannel | ''>('');
    const [status, setStatus]   = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo]   = useState('');
    const [limit, setLimit]     = useState(10);
    const [offset, setOffset]   = useState(0);
    const [appliedFilters, setAppliedFilters] = useState<CommunicationHistoryFilters>({});
    const [refreshKey, setRefreshKey]         = useState(0);

    const hasFilters = !!(channel || status || dateFrom || dateTo);

    const buildFilters = (): CommunicationHistoryFilters => ({
        ...(channel  && { channel }),
        ...(status   && { status }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo   && { dateTo }),
    });

    const fetchHistory = useCallback((filters: CommunicationHistoryFilters, lim: number, off: number) => {
        if (!customerId) return;
        setLoading(true);
        service
            .getCustomerCommunicationHistory(customerId, filters, lim, off)
            .then((res) => { setHistory(res.data); setTotal(res.count); })
            .catch(() => EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Failed to load communication history',
                type: CustomSnackbarTypes.ERROR,
            }))
            .finally(() => setLoading(false));
    }, [customerId]);

    useEffect(() => {
        fetchHistory(appliedFilters, limit, offset);
    }, [fetchHistory, appliedFilters, limit, offset, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handler = () => setRefreshKey(k => k + 1);
        EventBus.getInstance().on(EventBusEventsEnum.COMMUNICATION_MESSAGE_SENT, handler);
        return () => { EventBus.getInstance().off(EventBusEventsEnum.COMMUNICATION_MESSAGE_SENT, handler); };
    }, []);

    const handleApply = () => {
        setAppliedFilters(buildFilters());
        setOffset(0);
    };
    const handleClear = () => {
        setChannel(''); setStatus(''); setDateFrom(''); setDateTo('');
        setAppliedFilters({});
        setOffset(0);
    };

    const handlePageChange = (_: unknown, newPage: number) => setOffset(newPage * limit);
    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLimit(parseInt(e.target.value, 10));
        setOffset(0);
    };

    return (
        <Box sx={{ p: 2.5 }}>
            {/* ── Header ── */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography variant="h6" fontWeight={700} fontSize={16}>
                        Communication History
                    </Typography>
                    {!loading && (
                        <Chip
                            label={`${total} record${total !== 1 ? 's' : ''}`}
                            size="small"
                            sx={{ height: 22, fontSize: 11, bgcolor: 'grey.100', color: 'text.secondary', fontWeight: 500 }}
                        />
                    )}
                </Box>
                <Tooltip title="Refresh">
                    <span>
                        <IconButton size="small" onClick={() => setRefreshKey(k => k + 1)} disabled={loading}
                            sx={{ color: 'text.secondary' }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* ── Filter bar ── */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2, bgcolor: alpha('#f8fafc', 1) }}>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="flex-end" useFlexGap>
                    <FormControl size="small" sx={{ minWidth: 128 }}>
                        <InputLabel>Channel</InputLabel>
                        <Select label="Channel" value={channel} onChange={(e) => setChannel(e.target.value as any)}>
                            <MenuItem value="">All channels</MenuItem>
                            <MenuItem value={CommunicationChannel.SMS}>SMS</MenuItem>
                            <MenuItem value={CommunicationChannel.WHATSAPP}>WhatsApp</MenuItem>
                            <MenuItem value={CommunicationChannel.FCM}>In-App</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 128 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                            <MenuItem value="">All statuses</MenuItem>
                            <MenuItem value="DELIVERED">Delivered</MenuItem>
                            <MenuItem value="SENT">Sent</MenuItem>
                            <MenuItem value="PENDING">Pending</MenuItem>
                            <MenuItem value="FAILED">Failed</MenuItem>
                            <MenuItem value="BLOCKED">Blocked</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField size="small" label="From date" type="date" value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }}
                        sx={{ width: 148 }} />
                    <TextField size="small" label="To date" type="date" value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }}
                        sx={{ width: 148 }} />

                    <Box display="flex" gap={1} ml="auto !important">
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FilterAltOffIcon fontSize="small" />}
                            onClick={handleClear}
                            disabled={!hasFilters}
                            sx={{ borderColor: 'divider', color: 'text.secondary' }}
                        >
                            Clear
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            onClick={handleApply}
                            disabled={loading}
                        >
                            Apply filters
                        </Button>
                    </Box>
                </Stack>
            </Paper>

            {/* ── Table ── */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {['Channel', 'Template', 'Message', 'Status', 'Sent At'].map((h) => (
                                <TableCell key={h} sx={{
                                    fontWeight: 600, fontSize: 12, color: 'text.secondary',
                                    bgcolor: '#f8fafc', py: 1.25, textTransform: 'uppercase', letterSpacing: 0.4,
                                }}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonRows />
                        ) : history.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ border: 0 }}>
                                    <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1.5}>
                                        <Box sx={{
                                            width: 56, height: 56, borderRadius: '50%',
                                            bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <MarkEmailReadOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
                                        </Box>
                                        <Typography variant="body2" fontWeight={500} color="text.secondary">
                                            No communications found
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled">
                                            {hasFilters ? 'Try adjusting your filters' : 'No messages have been sent to this customer yet'}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            history.map((item, idx) => {
                                const ch = CHANNEL_META[item.channel as keyof typeof CHANNEL_META] ?? CHANNEL_META.SMS;
                                const st = STATUS_META[item.status?.toUpperCase()] ?? { color: 'default' as const, label: item.status || 'Unknown' };
                                return (
                                    <TableRow key={`${item.channel}-${item.id}`} hover
                                        sx={{ bgcolor: idx % 2 === 0 ? 'white' : alpha('#f8fafc', 0.6) }}>
                                        <TableCell sx={{ py: 1.25 }}>
                                            <Box display="flex" alignItems="center" gap={0.75}
                                                sx={{
                                                    bgcolor: ch.bg, color: ch.color,
                                                    px: 1, py: 0.4, borderRadius: 1,
                                                    width: 'fit-content', fontWeight: 600, fontSize: 12,
                                                }}>
                                                {ch.icon}
                                                {ch.label}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25 }}>
                                            <Tooltip title={item.templateName} placement="top-start">
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 150, fontWeight: 500 }}>
                                                    {item.templateName}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25 }}>
                                            <Tooltip title={item.message || ''} placement="top-start">
                                                <Typography variant="body2" color="text.secondary" sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    maxWidth: 320,
                                                    lineHeight: 1.5,
                                                }}>
                                                    {item.message || '—'}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25 }}>
                                            <Chip
                                                label={st.label}
                                                color={st.color}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontWeight: 600, fontSize: 11, height: 22 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                {item.sentAt ? formatDate(item.sentAt) : '—'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                count={total}
                                page={Math.floor(offset / limit)}
                                rowsPerPage={limit}
                                rowsPerPageOptions={[10, 25, 50]}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    );
}
